import {
	Avatar,
	EmptyBox,
	GenericBadge,
	Icon,
	LoaderScreen,
	Typography,
} from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import { InputCheck } from '@/components/form';
import Icons from '@/const/icons';
import * as React from 'react';
import { useRouter } from 'next/router';
import { GET_CLIENT_DIRECTORY, lawyerDirectoryService } from '@/api';
import { CaseClientItem, CaseClientsCounter } from '@/interfaces';
import { format, parseISO } from 'date-fns';
import { useInfinityPagination } from '@/hooks/queries/useInfinityPagination';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { useCaseClientFilters } from '@/hooks/useCaseClientFilters';

export const CaseClientList: React.FC<{
	clientSelected?: CaseClientItem | null;
	setClientSelected?: React.Dispatch<
		React.SetStateAction<CaseClientItem | null>
	>;
	showCheckbox?: boolean;
	selectedUserIds?: (string | null)[];
	onSelectUser?: (userId: string | null, checked: boolean) => void;
	setTotalItems?: (data: CaseClientsCounter) => void;
	setCurrentUserIds?: (ids: string[]) => void;
	setApplicationIdByUserId?: (map: Record<string, number>) => void;
}> = ({
	clientSelected,
	setClientSelected,
	showCheckbox,
	selectedUserIds = [],
	onSelectUser,
	setTotalItems,
	setCurrentUserIds,
	setApplicationIdByUserId,
}) => {
	const lastIdsRef = React.useRef<string>('');
	const lastApplicationMapRef = React.useRef<string>('');
	const clientRefs = React.useRef<Map<string, HTMLLIElement>>(new Map());
	const LIMIT = 50;
	const router = useRouter();
	const { register } = useForm();
	const { id } = router.query;
	const { rawFilters, parsedFilters } = useCaseClientFilters();

	const {
		data: caseClientList,
		isLoading,
		lastItemCallback,
		isError,
		isFetchingNextPage,
	} = useInfinityPagination({
		queryKey: [GET_CLIENT_DIRECTORY, id, ...Object.values(rawFilters)],
		queryFn: ({ pageParam }) =>
			lawyerDirectoryService
				.getCaseClients({
					...parsedFilters,
					limit: LIMIT,
					clientId: rawFilters.clientId,
					segmentId: rawFilters.segmentId,
					isOrderAlph: !!rawFilters.isOrderAlph,
					page: pageParam || 1,
					caseId: id as string,
					totalCase: rawFilters.totalCase,
					totalTaskClient: rawFilters.totalTaskClient,
					totalTaskLawyer: rawFilters.totalTaskLawyer,
					actionStatusLawyer: rawFilters.actionStatusLawyer,
					actionStatusClient: rawFilters.actionStatusClient,
					totalMessage: rawFilters.totalMessage,
				})
				.then((res) => res.data),
	});

	const handleSort = (field: 'NAME' | 'STATUS_DATE') => {
		let newSort = '';

		if (field === 'NAME') {
			newSort = rawFilters.sort === 'NAME_ASC' ? 'NAME_DESC' : 'NAME_ASC';
		} else if (field === 'STATUS_DATE') {
			newSort =
				rawFilters.sort === 'STATUS_DATE_DESC'
					? 'STATUS_DATE_ASC'
					: 'STATUS_DATE_DESC';
		}

		router.push(
			{
				pathname: router.pathname,
				query: { ...router.query, sort: newSort },
			},
			undefined,
			{ shallow: true, scroll: false }
		);
	};

	// Set total items for the parent component
	React.useEffect(() => {
		if (caseClientList?.pages?.[0]) {
			setTotalItems?.({
				total: caseClientList.pages[0].totalItems ?? 0,
				all: caseClientList.pages[0].counters.total ?? 0,
				complete: caseClientList.pages[0].counters.complete ?? 0,
				pending: caseClientList.pages[0].counters.pending ?? 0,
				review: caseClientList.pages[0].counters.review ?? 0,
				rejected: caseClientList.pages[0].counters.rejected ?? 0,
			});
		}
		if (caseClientList?.pages) {
			const allIds = caseClientList.pages.flatMap((page) =>
				page.items.map((client) => client.user_id || '')
			);
			const applicationIdMap = caseClientList.pages.reduce<
				Record<string, number>
			>((acc, page) => {
				page.items.forEach((client) => {
					if (client.user_id && client.application_id != null) {
						acc[client.user_id] = client.application_id;
					}
				});
				return acc;
			}, {});

			const idsString = JSON.stringify({ caseId: id, userIds: allIds });
			if (idsString !== lastIdsRef.current) {
				lastIdsRef.current = idsString;
				setCurrentUserIds?.(allIds);
			}

			const mapFingerprint = JSON.stringify({
				caseId: id,
				map: applicationIdMap,
			});
			if (mapFingerprint !== lastApplicationMapRef.current) {
				lastApplicationMapRef.current = mapFingerprint;
				setApplicationIdByUserId?.(applicationIdMap);
			}
		}
	}, [
		caseClientList,
		id,
		setTotalItems,
		setCurrentUserIds,
		setApplicationIdByUserId,
	]);

	const flatClients = React.useMemo(
		() => caseClientList?.pages.flatMap((page) => page.items) || [],
		[caseClientList]
	);

	React.useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
			if (!flatClients.length) return;

			const target = e.target as HTMLElement;
			const isTypingContext =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable;
			if (isTypingContext) return;

			e.preventDefault();

			const currentIndex = flatClients.findIndex(
				(client) => client.user_id === clientSelected?.user_id
			);

			let nextIndex: number;
			if (currentIndex === -1) {
				nextIndex = 0;
			} else if (e.key === 'ArrowDown') {
				nextIndex = Math.min(currentIndex + 1, flatClients.length - 1);
			} else {
				nextIndex = Math.max(currentIndex - 1, 0);
			}

			setClientSelected?.(flatClients[nextIndex]);
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [flatClients, clientSelected, setClientSelected]);

	React.useEffect(() => {
		if (!clientSelected?.user_id) return;
		const node = clientRefs.current.get(clientSelected.user_id);
		node?.scrollIntoView({ block: 'nearest' });
	}, [clientSelected]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center lg:min-h-[350px]">
				<LoaderScreen />
			</div>
		);
	}

	const hasFilters =
		!!rawFilters.search ||
		!!rawFilters.clientId ||
		!!rawFilters.actionStatusLawyer ||
		!!rawFilters.actionStatusClient ||
		!!rawFilters.messageFilter ||
		!!rawFilters.segmentId ||
		!!rawFilters.step ||
		!!rawFilters.dateFrom ||
		!!rawFilters.dateTo ||
		!!rawFilters.status ||
		!!rawFilters.documentFilter ||
		!!rawFilters.claimFormQuestions ||
		!!rawFilters.flagged ||
		!!rawFilters.documents;

	if (caseClientList && caseClientList?.pages[0].items.length === 0) {
		if (hasFilters) {
			return (
				<div className="flex flex-col items-center justify-center mt-12">
					<Icon src={Icons.filterEmptyState} width={174} height={139} />
					<Typography
						type="regular-18"
						className="font-normal text-center px-8 md:px-0"
					>
						There are currently no cases matching the selected filters.
					</Typography>
					<Typography
						type="regular-16"
						className="font-normal text-center text-opacity-black mt-2"
					>
						Please try adjusting your filter.
					</Typography>
				</div>
			);
		}

		return (
			<div className="flex items-center justify-center lg:min-h-[350px]">
				<EmptyBox
					message="This case does not yet have any clients."
					width={250}
					height={200}
				/>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex items-center justify-center lg:min-h-[350px]">
				<EmptyBox message="Something went wrong. Please try again later." />
			</div>
		);
	}

	const allVisibleIds =
		caseClientList?.pages.flatMap((page) =>
			page.items.map((client) => client.user_id || '')
		) || [];

	const isAllSelected =
		selectedUserIds.includes(null as any) ||
		(allVisibleIds.length > 0 &&
			allVisibleIds.every((id) => selectedUserIds.includes(id)));

	return (
		<ul className="divide-y divide-grey-100 relative">
			{/* Table Header */}
			<li className="grid grid-cols-1 gap-2 lg:grid-cols-[2fr_44px_1fr_1fr_1fr_0.5fr] px-1 items-center mt-1">
				<div className="flex items-center gap-3 min-w-0">
					{showCheckbox && (
						<div onClick={(e) => e.stopPropagation()}>
							<InputCheck
								name="selectAll"
								checked={isAllSelected}
								onChange={(e) => {
									onSelectUser?.(null, e.target.checked);
								}}
								register={register}
								className="!mb-0"
							/>
						</div>
					)}
					<button
						className="flex items-center gap-2 group"
						onClick={() => handleSort('NAME')}
					>
						<Typography
							type="regular-12"
							className="text-opacity-black text-left"
						>
							Client Name
						</Typography>
						<Icon
							src={Icons.caretUpDown}
							width={16}
							height={16}
							className={clsx(
								'group-hover:text-black',
								(rawFilters.sort === 'NAME_ASC' ||
									rawFilters.sort === 'NAME_DESC') &&
									'text-black'
							)}
						/>
					</button>
				</div>
				<div className="w-6 shrink-0"></div>
				<div className="mx-auto">
					<Typography type="regular-12" className="text-opacity-black">
						Status
					</Typography>
				</div>
				<div>
					<Typography type="regular-12" className="text-opacity-black">
						Step
					</Typography>
				</div>
				<button
					className="flex items-center gap-2 group"
					onClick={() => handleSort('STATUS_DATE')}
				>
					<Typography type="regular-12" className="text-opacity-black">
						Last Activity
					</Typography>
					<Icon
						src={Icons.caretUpDown}
						width={16}
						height={16}
						className={clsx(
							'group-hover:text-black',
							(rawFilters.sort === 'STATUS_DATE_ASC' ||
								rawFilters.sort === 'STATUS_DATE_DESC') &&
								'text-black'
						)}
					/>
				</button>
				<div></div>
			</li>

			{caseClientList?.pages.map((page, pageIndex) =>
				page.items.map((client, clientIndex) => {
					const isLastItem =
						pageIndex === caseClientList.pages.length - 1 &&
						clientIndex === page.items.length - 1;

					return (
						<li
							ref={(node) => {
								if (client.user_id) {
									if (node) {
										clientRefs.current.set(client.user_id, node);
									} else {
										clientRefs.current.delete(client.user_id);
									}
								}
								if (isLastItem) lastItemCallback?.(node);
							}}
							onClick={() => setClientSelected?.(client)}
							key={`${pageIndex}-${clientIndex}`}
							className={clsx(
								'grid grid-cols-1 gap-2 lg:grid-cols-[2fr_44px_1fr_1fr_1fr_0.5fr] py-2 my-1 hover:bg-grey-5 px-1 rounded items-center',
								clientSelected?.user_id === client.user_id && 'bg-grey-5',
								!showCheckbox && 'cursor-pointer'
							)}
						>
							<div
								className="flex items-center gap-3 min-w-0"
								title={`${client.first_name} ${client.last_name}`}
							>
								{showCheckbox && (
									<div onClick={(e) => e.stopPropagation()}>
										<InputCheck
											name={`client-${client.user_id}`}
											checked={
												selectedUserIds.includes(null as any) ||
												selectedUserIds.includes(client.user_id || '')
											}
											onChange={(e) => {
												onSelectUser?.(client.user_id || '', e.target.checked);
											}}
											register={register}
											className="!mb-0"
										/>
									</div>
								)}
								<Avatar
									photoUrl={client.profile_picture || ''}
									firstname={client.first_name || ''}
									lastname={client.last_name || ''}
									size="extra-small"
								/>
								<Typography
									type="semibold-14"
									className="text-black text-left truncate"
								>
									{client.first_name} {client.last_name}
								</Typography>
							</div>
							<div className="flex items-center justify-start gap-1">
								{client.flagged && (
									<>
										<TooltipComponent
											label={client.flagged_reason?.join(', ') ?? ''}
											classNameLabel="!font-bold !text-center"
											className="!max-w-[200px]"
											align="center"
											alignOffset={0}
											showArrow={true}
											side="top"
											trigger={
												<span>
													<Icon
														src={Icons.flagFilled}
														width={16}
														height={16}
														className="shrink-0"
													/>
												</span>
											}
										/>
										{!!client.flagged_reason?.length && (
											<span
												className="flex items-center justify-center shrink-0 p-[2px] text-primary text-[10px] font-semibold"
												style={{
													width: 17,
													height: 15,
													borderRadius: 32,
													backgroundColor: '#8039DF1A',
												}}
											>
												{String(client.flagged_reason.length).padStart(2, '0')}
											</span>
										)}
									</>
								)}
							</div>
							<div className="mx-auto">
								<GenericBadge
									colorStatus={
										client.status === 'Complete'
											? 'green'
											: client.status === 'Pending'
											? 'orange'
											: client.status === 'Rejected'
											? 'gray'
											: 'red'
									}
									label={client.status}
									typographyType="semibold-12"
									classNameContainer="!py-1 !px-2 !rounded-full !w-fit"
								/>
							</div>
							<Typography type="regular-12" className="text-opacity-black">
								{client.stepper_status}
							</Typography>
							<div className="flex items-center gap-2">
								<Icon
									src={Icons.calendar}
									className="text-opacity-black"
									width={18}
									height={18}
								/>
								<Typography type="regular-12" className="text-opacity-black">
									{client.status_date
										? format(parseISO(client.status_date), 'MM-dd-yyyy')
										: '-'}
								</Typography>
							</div>

							<div className="flex items-center lg:justify-center">
								{client.has_unread_messages === true ? (
									<div className="relative lg:mx-auto">
										<Icon
											src={Icons.message}
											className="text-opacity-black"
											width={18}
											height={18}
										/>
										<span className="bg-main-red rounded-full w-2 h-2 ring-white ring-2 absolute -top-0.5 -right-1" />
									</div>
								) : client.has_unread_messages === false ? (
									<div className="lg:mx-auto">
										<Icon
											src={Icons.arrowUpRight}
											width={18}
											height={18}
											className="text-opacity-black"
										/>
									</div>
								) : null}
							</div>
						</li>
					);
				})
			)}
			{isFetchingNextPage && (
				<div className="mx-auto py-4">
					<LoaderScreen />
				</div>
			)}
		</ul>
	);
};
